type TokenGetter = () => Promise<string>;

let auth0TokenGetter: TokenGetter | null = null;

const AUTH0_LOCAL_STORAGE_PREFIX = '@@auth0spajs@@';
const AUTH0_TRANSACTION_PREFIX = 'a0.spajs.txs';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorText = (error: unknown): string => {
  if (!isObject(error)) {
    return String(error ?? '');
  }

  const fields = ['error', 'error_description', 'message'];
  return fields
    .map((field) => error[field])
    .filter((value): value is string => typeof value === 'string')
    .join(' ');
};

const isInvalidAuth0RefreshTokenError = (error: unknown): boolean => {
  const text = getErrorText(error).toLowerCase();
  return text.includes('invalid_grant') && text.includes('refresh token');
};

const clearAuth0Cache = () => {
  [localStorage, sessionStorage].forEach((storage) => {
    Object.keys(storage).forEach((key) => {
      if (key.startsWith(AUTH0_LOCAL_STORAGE_PREFIX) || key.startsWith(AUTH0_TRANSACTION_PREFIX)) {
        storage.removeItem(key);
      }
    });
  });
};

export const setAuth0TokenGetter = (getter: TokenGetter) => {
  auth0TokenGetter = getter;
};

export const getAuth0Token = async (): Promise<string | null> => {
  if (auth0TokenGetter) {
    try {
      return await auth0TokenGetter();
    } catch (error) {
      if (isInvalidAuth0RefreshTokenError(error)) {
        clearTokenInfo();
      }

      console.error('Error getting Auth0 token:', error);
      return null;
    }
  }
  return null;
};

export const getAccessToken = async () => {
  const auth0Token = await getAuth0Token();
  if (auth0Token) {
    return auth0Token;
  }

  const token = localStorage.getItem('authToken');
  if (token) {
    return token;
  } else {
    console.error('No access token found in localStorage');
    return null;
  }
};

export const clearTokenInfo = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('expireToken');
  clearAuth0Cache();
};
